import React, { useState, useEffect } from "react";
import TooltipTrigger from "../bootstrap/TooltipTrigger";
import UserCard from "../users/UserCard";
import Col from "react-bootstrap/Col";
import Row from 'react-bootstrap/Row';
import Dropdown from "react-bootstrap/Dropdown";
import Collapse from "react-bootstrap/Collapse";
import {createElementFromHTML, formatDate} from "../../utils/utilsHelpers";
import {sanitizeRedactorContent} from "../../utils/postingHelpers";
import CommentActions from "./CommentActions";
import { JSON_HEADERS } from "../../constants/constants";
import ReportContent from "../actions/ReportContent";
import ConfirmModal from "../modals/ConfirmModal";
import ResponseModal from "../modals/ResponseModal";
import CommentUpdate from "./CommentUpdate";
import CommentForm from "./CommentForm";
import ReplyCard from "../replies/ReplyCard";
import {userTaggingMarkup} from "../../../javascripts/user_tagging_utils";

export default function CommentCard(props) {
    let {
        comment,
        postingId,
        postingAuthorId,
        currentUser={},
        showDate,
        showNewReplyForm=true,
        showReplies=true,
        readOnly=false,
        showCommentActions=true,
        token,
        cardClasses='',
        toggleRegistration=()=>{},
        leadData={}
    } = props;

    // Also in props: onDeleteComment
    const [thisComment, setThisComment] = useState(comment);
    const [featured, setFeatured] = useState(thisComment.featured);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeleteResponseModal, setShowDeleteResponseModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [adminMenuOpen, setAdminMenuOpen] = useState(false);
    const [replies, setReplies] = useState(thisComment.replies);
    const [showReplyForm, setShowReplyForm] = useState(!!replies.length && showNewReplyForm);
    const [showMoreReplies, setShowMoreReplies] = useState(comment.reply_count > 1);
    const [replyCount, setReplyCount] = useState(comment.reply_count);

    const onToggleReplyForm = (show) => {
        setShowReplyForm(show);
    }

    useEffect(() => {
        const timer = setTimeout(() => {
            setAdminMenuOpen(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [featured]);

    useEffect(() => {
        setThisComment(comment);
        setReplies(comment.replies);
        setShowMoreReplies(comment.reply_count > 1);
        setReplyCount(comment.reply_count);
        setShowReplyForm(!!comment.replies.length && showNewReplyForm);
    }, [comment]);

    const userSignedIn = !!currentUser;
    const viewingSelf = currentUser?.id == thisComment.author.id;
    const adminUser = currentUser?.admin;
    const isModerator = currentUser?.id == postingAuthorId;

    // event handlers

    const onSetFeatured = async() => {
        setAdminMenuOpen(true);
        await fetch(thisComment.actions.set_featured, {
            method: 'PATCH',
            headers: JSON_HEADERS,
            body: JSON.stringify({ authenticity_token: token, featured: !featured })
        });
        setFeatured(!featured);
    }

    const onAdminMenuToggle = (show, e) => {
        if(!e.originalEvent.target.classList.contains('set-featured')) {
            setAdminMenuOpen(show);
        }
    }

    const onDeleteHandler = async() => {
        await fetch(thisComment.actions.delete, {
            method: 'DELETE',
            headers: JSON_HEADERS,
            body: JSON.stringify({ authenticity_token: token })
        });

       setShowDeleteModal(false);
       setShowDeleteResponseModal(true);
       props.onDeleteComment(thisComment.id);
    }

    const onCommentUpdate = (uComment) => {
        setThisComment({ ...thisComment, body: uComment.body})
        setIsEditing(false);
    }

    const onCommentUpdateCancel = () => {
        setIsEditing(false);
    }

    const onCancelConfirm = () => setShowDeleteModal(false);
    const onHandleDeleteResponse = () => setShowDeleteResponseModal(false);

    const onNewReply = (reply) => {
        let newReplies = [...replies, reply];
        setReplies(newReplies);
        let newComment = { ...thisComment, replies: newReplies, reply_count: replyCount + 1 }
        props.onRepliesUpdate(newComment);
    }

    const onDeleteReply = (replyId) => {
        let currReplies = [...replies];
        let newReplies = currReplies.filter((r) => r.id != replyId);
        setReplies(newReplies);
        let newComment = { ...thisComment, replies: newReplies, reply_count: replyCount - 1 }
        props.onRepliesUpdate(newComment);
    }

    const onListReplies = async() => {

        await fetch(`/replies/list?comment_id=${comment.id}`, {
            method: 'GET',
            headers: JSON_HEADERS
        }).then(function(response) {
            return response.json();
        }).then(function(jsonResponse) {
            setReplies(jsonResponse.replies);
            setShowMoreReplies(false);
        });
    }

    const renderDeleteConfirm = () => {
        if(showDeleteModal) {
            return (
                <ConfirmModal
                    id={thisComment.id}
                    onConfirm={onDeleteHandler}
                    onCancelConfirm={onCancelConfirm}
                    header={'Are you sure?'}
                    body={'This change will remove this comment and all associated replies.'}
                    confirmTxt={'Confirm delete'}
                    show={showDeleteModal} />
            )
        }
    }

    const renderDeleteResponseModal = () => {
        if(showDeleteResponseModal) {
            return (
                <ResponseModal
                    modalSize={'md'}
                    bgColor={'white'}
                    textColor={'secondary'}
                    btnColor={'primary'}
                    icon={'check-circle'}
                    header={'Confirmed'}
                    message={'Your comment has been removed.'}
                    show={showDeleteResponseModal}
                    handleShow={onHandleDeleteResponse} />
            )
        }
    }

    // render methods

    const renderAdminMenu = () => {
        return (
            <Col className={'text-center d-none d-md-block ms-1'}>
                <Dropdown show={adminMenuOpen} onToggle={(show, e) => onAdminMenuToggle(show, e)}>
                    <Dropdown.Toggle className={'link-medium px-2'} as={'div'}>
                        <i className={'fas fa-ellipsis-v'}></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu align={'end'} className={'rounded font-13 py-1 mt-1'}>
                        <Dropdown.Item
                            href={thisComment.author.url}
                            className={'border-bottom'}
                        >
                            <i className={'fal fa-user me-2'}></i>
                            Author profile
                        </Dropdown.Item>

                        {(viewingSelf || adminUser) && <Dropdown.Item
                            // href={thisComment.actions.edit}
                            onClick={() => setIsEditing(true) }
                            className={'border-bottom'}
                        >
                            <i className={'fal fa-pencil me-2'}></i>
                            Edit
                        </Dropdown.Item>}

                        {(adminUser || isModerator) && <Dropdown.Item
                            onClick={() => onSetFeatured()}
                            className={`set-featured border-bottom ${featured && 'bg-primary'}`}
                        >
                            <i className={`fal fa-square-${ featured ? 'minus' : 'check'} me-2`}></i>
                            { featured? 'Remove' : 'Set as'} featured {thisComment.of_type}
                        </Dropdown.Item>}

                        {(adminUser || viewingSelf) && <Dropdown.Item
                            onClick={() => setShowDeleteModal(true)}
                            className={'border-bottom'}
                        >
                            <i className={'fal fa-trash-alt me-2'}></i>
                            Delete
                        </Dropdown.Item>}

                        <Dropdown.Item className={'border-bottom'} as={'div'}>
                            <ReportContent
                                item={comment}
                                itemType={'comment'}
                                itemUrl={thisComment.actions.posting_url}
                                linkClasses={'ms-0 text-gray-dark'}
                                userSignedIn={userSignedIn}
                                token={token}
                                toggleRegistration={toggleRegistration}
                                leadData={leadData}/>
                        </Dropdown.Item>

                    </Dropdown.Menu>
                </Dropdown>
            </Col>

        )
    }

    const renderDate = () => {
        if(showDate)
            return (
                <Row>
                    <Col xs={12} className={'mt-2 gray-600 font-14 content-styling'}>
                        {formatDate(thisComment.published_date)}
                    </Col>
                </Row>
            )
    }

    const renderCommentBody = () => {
        if(!isEditing) {
            let collapsibleVars = {
                '--collapsible-more': `'Read full ${thisComment.of_type}'`,
                '--collapsible-less': `'Close ${thisComment.of_type}'`,
                '--collapsible-show-lines': 4
            }
            return (
                <Row className={'mx-0 px-0 py-3 border-bottom'}>
                    <Col xs={12} className={'px-0 comment-body collapsible secondary-links content-cleanup content-styling'} style={collapsibleVars}>
                        {createElementFromHTML(sanitizeRedactorContent(thisComment.body))}
                    </Col>
                </Row>
            )
        }
    }

    const renderEditForm = () => {
        if(isEditing) {
            return (
                <div className={'mt-3'}>
                    <CommentUpdate
                        comment={comment}
                        currentUser={currentUser}
                        token={token}
                        onCommentUpdate={onCommentUpdate}
                        onCommentUpdateCancel={onCommentUpdateCancel}
                    />
                </div>
            )
        }
    }

    const renderCommentActions = () => {
        if(showCommentActions) {
            return (
                <CommentActions
                    comment={comment}
                    currentUser={currentUser}
                    token={token}
                    showReplies={showReplies}
                    onToggleReplyForm={onToggleReplyForm}
                    replyCount={replies.length}
                    hoverBg={'light'}
                    toggleRegistration={toggleRegistration}
                    leadData={leadData}
                />
            )
        }
    }

    const renderReplies = () => {
        if(showReplies) {
            return (
                <Row>
                    <div className={'comment-replies'}>
                        { replies.map( (reply, i) => {
                            return (
                                <ReplyCard
                                    key={reply.id}
                                    reply={reply}
                                    commentId={comment.id}
                                    commentAuthor={comment.author}
                                    currentUser={currentUser}
                                    token={token}
                                    onDeleteReply={onDeleteReply}
                                    lastItem={i == (replies.length - 1)}
                                    toggleRegistration={toggleRegistration}
                                    leadData={leadData}
                                />
                            )})
                        }
                        {showMoreReplies && <div className={'fs-6'}>
                            <i className={'fal fa-comment-alt text-primary me-2'}></i>
                            <a
                                onClick={() => onListReplies()}
                                className={'text-secondary'}>
                                See all {replyCount} replies
                            </a>
                        </div>}
                    </div>
                </Row>
            )
        }
    }

    const renderReplyForm = () => {
        if(showNewReplyForm && userSignedIn && (showReplyForm || replies.length > 0)) {
            return (
                <Collapse in={showReplyForm || replies.length > 0}>
                    <div id={'toggleAnswerForm'} className={'my-2'}>
                        <CommentForm
                            currentUser={currentUser}
                            parentId={thisComment.id}
                            parentType={'comment'}
                            token={token}
                            onNewComment={onNewReply}
                            type={'Reply'}
                            appendContent={`${userTaggingMarkup(thisComment.author.url, thisComment.author.username)}<span>&nbsp;</span>`}
                            toggleRegistration={toggleRegistration}
                            leadData={leadData}
                        />
                    </div>
                </Collapse>
            )
        }
    }

    return (
        <div className={`comment-card published-${!!thisComment.published_date} ${cardClasses}`}>
            <a className={'position-relative after-topbar'} name={`comment-${thisComment.id}`} id={`comment-${thisComment.id}`} />
            {featured && <TooltipTrigger id={`comment_featured_${thisComment.id}`} placement={'top'} content={'Featured answer'}>
                <div className={'featured'}>
                    <i className={'fas fa-circle-check fs-2 text-primary'}></i>
                </div>
            </TooltipTrigger>}
            <Row className={'align-items-center flex-nowrap'}>
                <div className={`${userSignedIn ? 'col-11' : 'col-12'}`}>
                    <UserCard user={thisComment.author} avatarSize={30} nameFs={15} />
                </div>
                {userSignedIn && !readOnly && renderAdminMenu()}
            </Row>
            {renderDate()}
            {renderCommentBody()}
            {renderEditForm()}
            {renderCommentActions()}
            {renderReplies()}
            {renderReplyForm()}
            {renderDeleteConfirm()}
            {renderDeleteResponseModal()}
        </div>
    )
}